import React, { useState, useEffect } from "react";
import { Container, Form, Row, Col, Modal, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "./UnifiedFormStyles.css";
import AppButton from "../../Components/MerchantButton";
import { submitForm } from "../../Helpers/API/formsAPI";
import { getRegionConfig } from "./config/regionConfig";
import { compressAndUpload } from "../../Helpers/Utils/photoUpload";

function AwardingDetails({ navigation, primaryDetails, setPrimaryDetails }) {
  const navigate = useNavigate();
  const [isError, setIsError] = useState({
    received_maya_credit: false,
    maya_account_name_photo: false,
    maya_account_number_photo: false,
    maya_account_position_photo: false,
    credit_certificate: false,
    is_credit_satisfied: false,
    maya_credit_feedback: false,
    credit_option_prize: false,
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const handlePhotoUpload = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Photo exceeds 5MB limit. Please retake the photo.", {
        autoClose: 3000, hideProgressBar: true, theme: "colored"
      });
      e.target.value = '';
      return;
    }

    setIsUploading(true);
    try {
      const uploadResult = await compressAndUpload(file);
      if (!uploadResult.success) {
        toast.error(uploadResult.error || "Upload failed. Please try again.");
        e.target.value = '';
        return;
      }
      setPrimaryDetails(prev => ({
        ...prev,
        [`${fieldName}_temp_path`]: uploadResult.temp_path
      }));
      if (isError[fieldName]) {
        setIsError(prev => ({ ...prev, [fieldName]: false }));
      }
    } catch (error) {
      toast.error("Error uploading photo");
    } finally {
      setIsUploading(false);
    }
  };

  // Sync to primaryDetails whenever state changes
  useEffect(() => {
    setPrimaryDetails((prev) => ({
      ...prev,
    }));
  }, [setPrimaryDetails]);

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    if (type === "file") return; // photos handled by handlePhotoUpload
    setPrimaryDetails(prev => ({ ...prev,
      [name]: value,
    }));
    if (isError[name]) {
      setIsError({
        ...isError,
        [name]: false,
      });
    }
  };

  const validateForm = () => {
    const errors = {
      received_maya_credit_photo: !primaryDetails.received_maya_credit_photo_temp_path,
      maya_account_name_photo: !primaryDetails.maya_account_name_photo,
      maya_account_number_photo: !primaryDetails.maya_account_number_photo,
      amount_transferred: !primaryDetails.amount_transferred,
      reference_no: !primaryDetails.reference_no,
      maya_account_position_photo: !primaryDetails.maya_account_position_photo,
      upload_company_id: !primaryDetails.upload_company_id_temp_path,
      credit_certificate: !primaryDetails.credit_certificate_temp_path,
      is_credit_satisfied: !primaryDetails.is_credit_satisfied,
      maya_credit_feedback: !primaryDetails.maya_credit_feedback,
      credit_option_prize: !primaryDetails.credit_option_prize,
    };

    setIsError(errors);

    const hasErrors = Object.values(errors).some((error) => error);
    if (hasErrors) {
      toast.error("Please fill in all required fields.", {
        autoClose: 3000,
        hideProgressBar: true,
        theme: "colored",
      });
      return false;
    }
    return true;
  };

  const isFormValid = () => {
    return !!primaryDetails.received_maya_credit_photo_temp_path &&
      !!primaryDetails.maya_account_name_photo &&
      !!primaryDetails.maya_account_number_photo &&
      !!primaryDetails.amount_transferred &&
      !!primaryDetails.reference_no &&
      !!primaryDetails.maya_account_position_photo &&
      !!primaryDetails.upload_company_id_temp_path &&
      !!primaryDetails.credit_certificate_temp_path &&
      !!primaryDetails.is_credit_satisfied &&
      !!primaryDetails.maya_credit_feedback &&
      !!primaryDetails.credit_option_prize;
  };
  const handleNext = () => {
    if (validateForm()) {
      setShowConfirmModal(true);
    }
  };

    const handleConfirmSubmit = async () => {
    setIsSubmitting(true);
    try {
      const regionConfig = getRegionConfig(primaryDetails?.region);
      const res = await submitForm(primaryDetails, regionConfig);

      if (res?.data) {
        try {
          localStorage.removeItem('maya-form-data');
          localStorage.removeItem('maya-form-details');
          localStorage.removeItem('maya-form-current-step');
          localStorage.removeItem('maya-form-history');
          localStorage.setItem('form_completed', 'true');
        } catch (e) {}
        setShowConfirmModal(false);
        navigate('/timein', { replace: true });
      } else {
        toast.error("Submit failed. Please try again.", { autoClose: 2500, hideProgressBar: true, theme: "colored" });
      }
    } catch (err) {
      toast.error("Something went wrong while submitting.", { autoClose: 2500, hideProgressBar: true, theme: "colored" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelSubmit = () => {
    setShowConfirmModal(false);
  };

  const handleBack = () => {
    if (navigation && navigation.previous) {
      navigation.previous();
    }
  };

  return (
    <div className="form-bg">
      <Container>
        <ToastContainer />
        <Row className="justify-content-md-center">
          <Col md={10}>
            <div className="form-main-header">
              <h4 className="form-title">Awarding Details</h4>
              <p className="form-desc">Please provide the awarding details for Maya Credits</p>
            </div>
            <div className="form-main">
              <div className="form-main-content">
                <Form>

                  {/* Upload received Maya Credits photo (from the merchant) */}
                  <Form.Group className="mb-4">
                    <Form.Label className="form-label">
                      Upload Proof of transfer from Sender to merchant photo (from the Sender) <span className="form-required">*</span>
                      {primaryDetails.received_maya_credit_photo_temp_path && !isUploading && (
                        <span style={{ marginLeft: '10px', color: '#28a745' }}>✅ Uploaded</span>
                      )}
                    </Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={(e) => handlePhotoUpload(e, 'received_maya_credit_photo')}
                      isInvalid={isError.received_maya_credit_photo}
                      disabled={isUploading}
                    />
                    {isUploading && <div style={{ color: '#007bff', fontSize: '13px', marginTop: '6px' }}>⏳ Uploading...</div>}
                    {isError.received_maya_credit_photo && <div className="form-error">Required</div>}
                  </Form.Group>

                  {/* Maya Account Name (Receiver of Credit) */}
                  <Form.Group className="mb-4" controlId="maya_account_name_photo">
                    <Form.Label className="form-label">
                      Maya Account Name (Receiver of Credit) <span className="form-required">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="maya_account_name_photo"
                      value={primaryDetails.maya_account_name_photo || ""}
                      onChange={handleInputChange}
                      placeholder="Enter Maya account name"
                      className={`form-input ${isError.maya_account_name_photo ? "error" : ""}`}
                    />
                    {isError.maya_account_name_photo && <div className="form-error">Required</div>}
                  </Form.Group>

                  {/* Maya Account No. (Receiver of Credit) */}
                  <Form.Group className="mb-4" controlId="maya_account_number_photo">
                    <Form.Label className="form-label">
                      Maya Account No. (Receiver of Credit) <span className="form-required">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="maya_account_number_photo"
                      value={primaryDetails.maya_account_number_photo || ""}
                      onChange={handleInputChange}
                      placeholder="Enter Maya account number"
                      className={`form-input ${isError.maya_account_number_photo ? "error" : ""}`}
                    />
                    {isError.maya_account_number_photo && <div className="form-error">Required</div>}
                  </Form.Group>

                  {/* Amount Transferred */}
                  <Form.Group className="mb-4" controlId="amount_transferred">
                    <Form.Label className="form-label">
                      Amount Transferred <span className="form-required">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="amount_transferred"
                      value={primaryDetails.amount_transferred || ""}
                      onChange={handleInputChange}
                      placeholder="Enter amount transferred"
                      className={`form-input ${isError.amount_transferred ? "error" : ""}`}
                    />
                    {isError.amount_transferred && <div className="form-error">Required</div>}
                  </Form.Group>

                  {/* Reference No. */}
                  <Form.Group className="mb-4" controlId="reference_no">
                    <Form.Label className="form-label">
                      Reference No. <span className="form-required">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="reference_no"
                      value={primaryDetails.reference_no || ""}
                      onChange={handleInputChange}
                      placeholder="Enter reference number"
                      className={`form-input ${isError.reference_no ? "error" : ""}`}
                    />
                    {isError.reference_no && <div className="form-error">Required</div>}
                  </Form.Group>

                  {/* Position (Receiver of Credit) */}
                  <Form.Group className="mb-4" controlId="maya_account_position_photo">
                    <Form.Label className="form-label">
                      Position (Receiver of Credit) <span className="form-required">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="maya_account_position_photo"
                      value={primaryDetails.maya_account_position_photo || ""}
                      onChange={handleInputChange}
                      placeholder="Enter position"
                      className={`form-input ${isError.maya_account_position_photo ? "error" : ""}`}
                    />
                    {isError.maya_account_position_photo && (
                      <div className="form-error">Required</div>
                    )}
                  </Form.Group>
                                    
                  <Form.Group className="mb-4">
                    <Form.Label className="form-label">
                      Upload Company ID of the Receiver <span className="form-required">*</span>
                      {primaryDetails.upload_company_id_temp_path && !isUploading && (
                        <span style={{ marginLeft: '10px', color: '#28a745' }}>✅ Uploaded</span>
                      )}
                    </Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={(e) => handlePhotoUpload(e, 'upload_company_id')}
                      isInvalid={isError.upload_company_id}
                      disabled={isUploading}
                    />
                    {isUploading && <div style={{ color: '#007bff', fontSize: '13px', marginTop: '6px' }}>⏳ Uploading...</div>}
                    {isError.upload_company_id && <div className="form-error">Required</div>}
                  </Form.Group>
                  {/* <Form.Group className="mb-4">
                    <Form.Label className="form-label">
                      Position (Receiver of Credit) Upload Photo <span className="form-required">*</span>
                    </Form.Label>
                    <Form.Control
                      type="file"
                      name="maya_account_position_photo"
                      accept="image/*"
                      onChange={handleInputChange}
                      isInvalid={isError.maya_account_position_photo}
                    />
                    {isError.maya_account_position_photo && (
                      <div className="form-error">Required</div>
                    )}
                  </Form.Group> */}

                  {/* Upload Awarding Photo (CERTIFICATE+CREDITS) */}
                  <Form.Group className="mb-4">
                    <Form.Label className="form-label">
                      Upload Awarding Photo (Certificate + Credits) <span className="form-required">*</span>
                      {primaryDetails.credit_certificate_temp_path && !isUploading && (
                        <span style={{ marginLeft: '10px', color: '#28a745' }}>✅ Uploaded</span>
                      )}
                    </Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={(e) => handlePhotoUpload(e, 'credit_certificate')}
                      isInvalid={isError.credit_certificate}
                      disabled={isUploading}
                    />
                    {isUploading && <div style={{ color: '#007bff', fontSize: '13px', marginTop: '6px' }}>⏳ Uploading...</div>}
                    {isError.credit_certificate && <div className="form-error">Required</div>}
                  </Form.Group>

                  {/* Is merchant satisfied with received Maya Credits? */}
                  <Form.Group className="mb-4" controlId="is_credit_satisfied">
                    <Form.Label className="form-label">
                      Is Merchant Satisfied with Received Maya Credits? <span className="form-required">*</span>
                    </Form.Label>
                    <Form.Select
                      name="is_credit_satisfied"
                      value={primaryDetails.is_credit_satisfied || ""}
                      onChange={handleInputChange}
                      className={`form-input ${isError.is_credit_satisfied ? "error" : ""}`}
                    >
                      <option value="">Select an option</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </Form.Select>
                    {isError.is_credit_satisfied && (
                      <div className="form-error">Please select an option.</div>
                    )}
                  </Form.Group>

                  {/* Verbatim Feedback */}
                  <Form.Group className="mb-4" controlId="maya_credit_feedback">
                    <Form.Label className="form-label">
                      Verbatim Feedback <span className="form-required">*</span>
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name="maya_credit_feedback"
                      value={primaryDetails.maya_credit_feedback || ""}
                      onChange={handleInputChange}
                      placeholder="Enter feedback"
                      className={`form-input ${isError.maya_credit_feedback ? "error" : ""}`}
                    />
                    {isError.maya_credit_feedback && (
                      <div className="form-error">Please enter feedback.</div>
                    )}
                  </Form.Group>

                  {/* If ever na may ibang options na prize, ano ang gusto ni merchant? */}
                  <Form.Group className="mb-4" controlId="credit_option_prize">
                    <Form.Label className="form-label">
                      If Ever May Ibang Options na Prize, Ano ang Gusto ni Merchant? <span className="form-required">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="credit_option_prize"
                      value={primaryDetails.credit_option_prize || ""}
                      onChange={handleInputChange}
                      placeholder="Enter preferred prize option"
                      className={`form-input ${isError.credit_option_prize ? "error" : ""}`}
                    />
                    {isError.credit_option_prize && (
                      <div className="form-error">Please enter preferred prize option.</div>
                    )}
                  </Form.Group>

                  {/* Navigation Buttons */}
                  <div className="form-btn-row">
                    <AppButton
                      onClick={handleBack}
                      variant="back"
                      className="form-btn variant-secondary"
                    >
                      ← Back
                    </AppButton>
                    <AppButton
                      onClick={handleNext}
                      variant="next"
                      className="form-btn variant-primary"
                      disabled={!isFormValid()}
                    >
                      Submit
                    </AppButton>
                  </div>

                </Form>
              </div>
            </div>
          </Col>
        </Row>

        {/* Confirmation Modal */}
        <Modal show={showConfirmModal} onHide={handleCancelSubmit} centered>
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title className="w-100 text-center fw-bold text-primary">
              ⚠️ Confirm Submission
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center py-4">
            <div className="mb-3">
              <i className="fas fa-exclamation-triangle text-warning" style={{fontSize: '3rem'}}></i>
            </div>
            <h5 className="mb-3 text-dark">Are you sure you want to proceed?</h5>
            <p className="text-muted mb-0">
              <strong>Once submitted, you can't go back to make changes.</strong>
            </p>
          </Modal.Body>
          <Modal.Footer className="border-0 justify-content-center pt-0">
            <Button 
              variant="outline-secondary" 
              onClick={handleCancelSubmit}
              className="px-4 me-3"
              style={{minWidth: '100px'}}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleConfirmSubmit}
              className="px-4"
              style={{minWidth: '100px', backgroundColor: '#007bff'}}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Submitting...
                </>
              ) : (
                'Yes, Proceed'
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
}

export default AwardingDetails;
